/**
 * Shared label helpers for the travel-health pages.
 *
 * These lived as copies inside RiskManagementPage, VaccinationsPage and
 * RecommendationsPage. The category lookup in particular was written as
 * `t[`riskCategory_${category}`]`, a dynamic index that cannot be type-checked
 * against Translation at all - which is how those keys went missing without
 * anyone noticing.
 */

import type { Translation } from "../types/i18n";

/**
 * Trims an ISO timestamp to its date part. `split("T")[0]` is `string |
 * undefined` under noUncheckedIndexedAccess even though it never is at runtime.
 */
export const toDateOnly = (iso: string): string => iso.split("T")[0] ?? iso;

/** Risk levels arrive from the API as plain strings; unknown values read low. */
export function riskLevelDescription(t: Translation, level: string): string {
  switch (level) {
    case "critical":
      return t.riskLevelDescCritical;
    case "high":
      return t.riskLevelDescHigh;
    case "medium":
      return t.riskLevelDescMedium;
    default:
      return t.riskLevelDescLow;
  }
}

/** An unrecognised category still renders a readable capitalised label. */
export function categoryLabel(t: Translation, category: string): string {
  switch (category) {
    case "vaccination":
      return t.riskCategoryVaccination;
    case "medication":
      return t.riskCategoryMedication;
    case "precaution":
      return t.riskCategoryPrecaution;
    case "advisory":
      return t.riskCategoryAdvisory;
    case "warning":
      return t.riskCategoryWarning;
    default:
      return category.charAt(0).toUpperCase() + category.slice(1);
  }
}
